<template lang="pug">
Modal(
    :open="props.showCreateModal"
    @close="handleClose"
)
    .bq-modal-wrap
        .bq-modal-close(@click="handleClose" :class="{disabled: promiseRunning}") &times;

        form.bq-modal-step(@submit.prevent="createProject")
            h2 Create a new project
            p.bq-modal-desc Name your project and add a #[span.wordset base prompt:] #[span.wordset a short note] on what your data is and how you want the agent to #[span.wordset handle it.] #[span.wordset The plan comes next.]
            label.bq-field-label(for="createProjectName") Project name
            input#createProjectName.bq-modal-input(
                :placeholder="projectNamePlaceholder"
                maxlength="256"
                type="text"
                required
                :value="newServiceName"
                @input="newServiceName = $event.target.value"
                :disabled="promiseRunning"
                @keydown.enter.prevent="descriptionRef?.focus()"
            )
            label.bq-field-label(for="createProjectDescription") Base Prompt
            textarea#createProjectDescription.bq-modal-textarea(
                ref="descriptionRef"
                :placeholder="projectDescriptionPlaceholder"
                rows="3"
                :value="newServiceDescription"
                @input="newServiceDescription = $event.target.value"
                :disabled="promiseRunning"
            )
            RegionSelect(
                v-model="newServiceRegion"
                fieldId="createProjectRegion"
                :disabled="promiseRunning"
            )
            //- A plan picked on a landing plan card: no plan step, unless changed.
            p.bq-plan-picked(v-if="plan")
                | Plan: #[strong {{ planLabel(plan) }}]
                |
                a.bq-link(role="button" tabindex="0" :class="{ disabled: promiseRunning }" @click="changePlan" @keydown.enter.prevent="changePlan") [Change]
            button.btn(
                type="submit"
                :disabled="!newServiceName"
                :class="{ 'bq-btn--loading': promiseRunning }"
            )
                span.bq-loader(v-if="promiseRunning")
                template(v-else) {{ submitLabel }}
            .error(v-if="error" style="margin-top: 0.75rem")
                span {{ error }}

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, ref, watch } from "vue";

import Modal from "@/components/modal.vue";
import RegionSelect from "@/components/regionSelect.vue";
import { pickProjectPlaceholders } from "./project-list";
import { type NewProjectPlan, continueNewProject, planLabel, projectNameProblem } from "./new-project";

const router = useRouter();
const route = useRoute();

const props = defineProps<{
    showCreateModal: boolean;
    firstServiceName?: string;
    /** Preselected on a landing plan card; empty: the plan page follows. */
    initialPlan?: NewProjectPlan | "";
}>();
const emit = defineEmits(["close"]);

let promiseRunning = ref(false);
let newServiceName = ref("");
let newServiceDescription = ref("");
// Empty until the picker resolves the visitor's closest region (or the user
// picks another); createService falls back to that same default if it stays so.
let newServiceRegion = ref("");
let descriptionRef = ref();
let error = ref("");
let plan = ref<NewProjectPlan | "">(props.initialPlan || "");

const submitLabel = computed(() =>
    plan.value === "free" ? "+ Create" : plan.value ? "Continue to checkout" : "Continue",
);
const changePlan = () => {
    if (!promiseRunning.value) plan.value = "";
};

let projectNamePlaceholder = ref("");
let projectDescriptionPlaceholder = ref("");
const rollPlaceholders = () => {
    const p = pickProjectPlaceholders();
    projectNamePlaceholder.value = p.name;
    projectDescriptionPlaceholder.value = p.description;
};
rollPlaceholders();

let createProject = async () => {
    if (promiseRunning.value) return;
    error.value = "";

    const problem = projectNameProblem(newServiceName.value);
    if (problem) {
        error.value = problem;
        return;
    }

    promiseRunning.value = true;
    const details = {
        name: newServiceName.value.trim(),
        description: newServiceDescription.value.trim(),
        region: newServiceRegion.value,
    };
    const chosen = plan.value;
    try {
        // No plan yet: the plan page. Free: created and opened. Paid: checkout.
        if (!chosen) {
            promiseRunning.value = false;
            handleClose();
        }
        await continueNewProject(details, chosen, router);
        if (chosen === "free") {
            // Released BEFORE handleClose, which refuses to close while a request is running.
            promiseRunning.value = false;
            handleClose();
        }
        // A paid plan keeps the spinner while the browser leaves for checkout;
        // still here after a moment, the checkout did not start (a toast said why).
        else if (chosen) setTimeout(() => (promiseRunning.value = false), 3000);
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
        promiseRunning.value = false;
    }
};

const resetModalState = () => {
    promiseRunning.value = false;
    // Reopening the modal must not show the previous attempt's failure.
    error.value = "";
    newServiceDescription.value = "";
    newServiceRegion.value = "";
    plan.value = props.initialPlan || "";
    rollPlaceholders();

    if (!props.firstServiceName || !props.firstServiceName.trim()) {
        newServiceName.value = "";
    }
};

watch(
    () => props.showCreateModal,
    (newVisible) => {
        if (newVisible) {
            resetModalState();
        }
    }
);

const handleClose = () => {
    if (promiseRunning.value) return;
    resetModalState();
    emit("close");
};
</script>

<style scoped lang="less">
@import "@/assets/less/bq-tokens.less";
.btn {
    width: 100%;
}
// .bq-modal-wrap {
//     position: relative;
//     padding: 2.5rem;
//     font-family: @mono;
//     color: @ink;
//     // max-width: 28rem;
// }


.bq-modal-close {
    position: absolute;
    top: 1.25rem;
    right: 1.5rem;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    color: @muted;
    transition: color 0.15s;

    &:hover {
        color: @ink;
    }
}

.bq-modal-back {
    margin-bottom: 1.5rem;
    font-size: 0.85rem;
    color: @muted;
    cursor: pointer;
    display: inline-block;

    &:hover {
        color: @ink;
    }
}

.bq-modal-step {
    h2 {
        margin: 0 0 0.5rem;
        font-size: 1.3rem;
        font-weight: 600;
    }
}

.bq-modal-desc {
    margin: 0 0 1.5rem;
    font-size: 0.88rem;
    color: @muted;
    line-height: 1.6;
}

.bq-field-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: @ink;
    margin-bottom: 0.4rem;
    letter-spacing: 0.02em;
}

.bq-plan-picked {
    margin: 0 0 0.75rem;
    font-size: 0.82rem;
    color: @muted;

    strong {
        color: @ink;
        font-weight: 600;
    }

    // A margin, not a space: Vue drops the whitespace between the two elements.
    .bq-link {
        margin-left: 0.4em;
    }
}

.bq-region-field {
    margin-bottom: 0.75rem;
}

.bq-modal-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }
}

.bq-modal-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;
    resize: vertical;
    min-height: 4rem;
    word-wrap: break-word;
    overflow-x: hidden;
    white-space: pre-wrap;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.bq-modal-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.75rem 1.4rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font-family: @mono;
    font-size: 0.9rem;
    cursor: pointer;

    &:disabled {
        opacity: 0.3;
        cursor: default;
        pointer-events: none;
    }

    &::after {
        display: none;
    }
}
</style>
